import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Pagination, PermissionsProvider} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {TableSkeleton} from '@/components/Table/TableSkeleton';
import InvoiceCreateButton from '@/screens/Merchants/Invoices/InvoiceCreateButton';
import InvoicesTable from '@/screens/Merchants/Invoices/InvoicesTable';
import useInvoiceList from '@/screens/Merchants/Invoices/useInvoiceList';

type BusinessInvoicesTabProps = {
  businessId: string;
  businessName?: string;
  /** Test hook prefix; each rendered element namespaces under it. */
  dataCy?: string;
};

/**
 * Fee invoices for one merchant, on that merchant's own page. The second way in
 * to raising an invoice: the business is already known here, so the form fills
 * it in and locks it.
 *
 * Reuses the table and the create form from the global Invoices tab; only the
 * scoping and the missing filter bar differ, since filtering by merchant makes
 * no sense on a list that is already one merchant's.
 */
const BusinessInvoicesTab = ({
  businessId,
  businessName,
  dataCy,
}: BusinessInvoicesTabProps) => {
  const {
    isLoading,
    list,
    pageInfo,
    fetchList,
    refetchCurrentPage,
    displayPage,
    isPageReset,
  } = useInvoiceList({businessId, enabled: Boolean(businessId)});

  const renderList = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (!list.length) {
      return (
        <p
          data-cy={dataCy ? `${dataCy}-empty` : undefined}
          className="py-10 text-center text-gray-500 dark:text-gray-400"
        >
          No invoices raised for {businessName || 'this merchant'} yet.
        </p>
      );
    }

    return (
      <InvoicesTable
        items={list}
        onInvoiceUpdated={refetchCurrentPage}
        dataCy={dataCy}
      />
    );
  };

  return (
    <>
      <div className="flex md:justify-end">
        <PermissionsProvider
          permission={AfriexAdminPermission.BusinessInvoiceManage}
        >
          <InvoiceCreateButton
            onSuccess={refetchCurrentPage}
            lockedBusinessId={businessId}
            label="Create Invoice +"
            dataCy={dataCy ? `${dataCy}-create` : undefined}
            formDataCy={dataCy ? `${dataCy}-form` : undefined}
          />
        </PermissionsProvider>
      </div>

      <section className="pt-5 md:pt-5">{renderList()}</section>

      {list && list.length ? (
        <Pagination
          onPageChange={fetchList as PaginationProps['onPageChange']}
          total={pageInfo.total}
          isPageReset={isPageReset}
          currentPageOverride={displayPage}
        />
      ) : null}
    </>
  );
};

export default BusinessInvoicesTab;
